import { PutRecordInput } from 'aws-sdk/clients/kinesis';
import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

import { ILogFields, ISystemFields, TKinesisRecord } from './interfaces';

const getTimezoneOffset = (now: Date): string => {
  const tzOffset = now.getTimezoneOffset();
  const isTzFaster = tzOffset <= 0; // UTC+9 = -540 = 0900 : UTC-9 = 540 = -0900
  const tzHoursRaw = Math.abs(Math.floor(tzOffset / 60));
  const tzHours = tzHoursRaw.toString().padStart(2, '0');
  const tzMinutesRaw = Math.abs(Math.floor(tzOffset % 60));
  const tzMinutes = tzMinutesRaw.toString().padStart(2, '0');

  return `${isTzFaster ? '' : '-'}${tzHours}:${tzMinutes}`;
};

/**
 * UA에서 운영체제와 버전을 반환한다.
 *
 * @returns
 * 1. 이름과 버전 둘 다 알면 `'OS Version'`
 * 2. 이름만 알면 `'OS'`
 * 3. `'undefined'`
 */
export const getOSInfo = (uaOS: { name?: string; version?: string }): string => {
  if (!uaOS.name) {
    return 'undefined';
  }
  return `${uaOS.name} ${uaOS.version || ''}`.trim();
};

/**
 * runMode에서 env를 반환한다.
 *
 * @returns LIVE | STAGE | QA | DEV
 */
export const getEnv = (runMode: string): 'LIVE' | 'STAGE' | 'QA' | 'DEV' => {
  if (runMode === 'production') {
    return 'LIVE';
  }

  if (runMode === 'development') {
    return 'DEV';
  }

  return runMode.toUpperCase() as 'STAGE' | 'QA';
};

/**
 * UA에서 배급자와 모델명을 반환한다.
 *
 * @returns
 * 1. 배급자와 모델 둘 다 알 수 있으면 `'Vendor Model'`
 * 2. 모델만 알면 `'Model'`
 * 2. 배급자는 알지만 모델을 모르면 `'undefined (Vendor)'`
 * 3. 둘 다 모르지만 PC면 `'undefined (Generic PC)'`
 * 4. `'undefined'`
 */
export const getDeviceInfo = (webType: 'mweb' | 'web', uaDevice: { vendor?: string; model?: string }): string => {
  const getDeviceNameString = (): string => `${uaDevice.vendor || ''} ${uaDevice.model}`.trim();

  if (webType === 'mweb') {
    if (!uaDevice.model) {
      return uaDevice.vendor ? `undefined (${uaDevice.vendor})` : 'undefined';
    }
    return getDeviceNameString();
  }

  if (!uaDevice.model) {
    return uaDevice.vendor ? `undefined (${uaDevice.vendor})` : 'undefined (Generic PC)';
  }
  return getDeviceNameString();
};

/**
 * UA에서 브라우저명 및 버전을 반환한다.
 *
 * @returns
 * 1. 이름과 버전 둘 다 알 수 있으면 `'Name Version'`
 * 2. 이름만 알면 `'Name'`
 * 3. `'undefined'`
 */
export const getBrowserInfo = (uaBrowser: { name?: string; version?: string }): string => {
  if (!uaBrowser.name) {
    return 'undefined';
  }
  return `${uaBrowser.name} ${uaBrowser.version || ''}`.trim();
};

/**
 * 주어진 로그 데이터에 시스템 필드를 추가한다.
 */
const addSystemFields = (data: ILogFields): TKinesisRecord => {
  const now = new Date();

  const ua = new UAParser();
  const uaOS = ua.getOS();
  const uaDevice = ua.getDevice();
  const uaBrowser = ua.getBrowser();
  const webType = uaDevice.type === 'mobile' || uaDevice.type === 'tablet' ? 'mweb' : 'web';

  const intermediate: ISystemFields = {
    webType,
    browserInfo: getBrowserInfo(uaBrowser),
    cgntId: Cookies.get('cgntId')!,
    createdTs: Number(now),
    currentUri: window.location.href,
    deviceName: getDeviceInfo(webType, uaDevice),
    langCode: navigator.language,
    osInfo: getOSInfo(uaOS),
    referrer: document.referrer,
    resolution: `${window.innerWidth}x${window.innerHeight}`,
    timeZone: getTimezoneOffset(now),
    userAgent: navigator.userAgent,
    webVersion: window.ya__.version,
    env: getEnv(window.ya__.runMode),
  };

  // 회원 등급
  if (window.ya__.memberClass) {
    intermediate.mGrade = window.ya__.memberClass;
  }

  // 회원 번호 해시값
  if (window.ya__.hashedMemberNo) {
    intermediate.hashedMemberNo = window.ya__.hashedMemberNo;
  }

  // NOL 회원 추적 번호
  if (window.ya__.nolMemberProxyId) {
    intermediate.nolMemberProxyId = window.ya__.nolMemberProxyId;
  }

  return {
    ...data,
    ...intermediate,
  };
};

export const injectSysFields = (data: ILogFields): PutRecordInput => ({
  Data: JSON.stringify(addSystemFields(data)),
  PartitionKey: Cookies.get('cgntId')!,
  StreamName: window.ya__.streamName,
});
